// Dedicated import landing page. Two ways in:
//   1. Auto-import from a URL hash (#data=...) — opens directly to "Imported!"
//      state with a link to view the project. Used by the "Copy share link"
//      button on the project board.
//   2. Manual file picker for the JSON export-project download.
// Both paths call the same importBackup under the hood so behaviour is identical.
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Seo from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import { importBackup } from '../lib/storage'
import type { BackupData } from '../lib/storage'
import { readShareHash, decodeShareData } from '../lib/shareCodec'
import { useLocale, localizedHref } from '../i18n/locale'

interface ImportResult {
  projects: number
  tasks: number
  sheets: number
  firstProjectId?: string
}

export default function ImportPage() {
  const locale = useLocale()
  const navigate = useNavigate()
  const L = (en: string) => localizedHref(en, locale)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Auto-import from URL hash if present. Runs once on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const encoded = readShareHash(window.location.hash)
    if (!encoded) return
    setBusy(true)
    ;(async () => {
      try {
        const bundle = decodeShareData<BackupData>(encoded)
        const r = await importBackup(bundle)
        setResult({ ...r, firstProjectId: bundle.projects?.[0]?.id })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not read this share link.')
      } finally {
        setBusy(false)
        // Strip the hash from the URL so a refresh doesn't re-import.
        if (window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname)
        }
      }
    })()
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const data = JSON.parse(await file.text()) as BackupData
      const r = await importBackup(data)
      setResult({ ...r, firstProjectId: data.projects?.[0]?.id })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const isFr = locale === 'fr'
  const t = (en: string, fr: string) => (isFr ? fr : en)

  const goToProject = () => {
    if (result?.firstProjectId) {
      navigate(`${isFr ? '/fr' : ''}/my-sheets/project/${result.firstProjectId}`)
    } else {
      navigate(L('/my-sheets/'))
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Seo
        title={t('Import a RenoSheets project | RenoSheets', 'Importer un projet RenoSheets | RenoSheets')}
        description={t(
          'Import a RenoSheets project file shared with you. Open this page and pick the .json file — the project appears in your own browser, nothing leaves your device.',
          'Importez un fichier de projet RenoSheets qui vous a été partagé. Ouvrez cette page et choisissez le fichier .json — le projet apparaît dans votre navigateur, rien ne quitte votre appareil.',
        )}
        path="/import/"
      />
      <Breadcrumbs
        items={[
          { name: t('Home', 'Accueil'), href: L('/') },
          { name: t('Import project', 'Importer un projet'), href: L('/import/') },
        ]}
      />

      <h1 className="text-3xl">{t('Import a shared project', 'Importer un projet partagé')}</h1>
      <p className="mt-3 text-text-muted">
        {t(
          'Someone shared a RenoSheets project with you — either a .json file or a share link. Drop the file below (or open the share link) and the project will load into your own browser.',
          'Quelqu’un vous a partagé un projet RenoSheets — un fichier .json ou un lien de partage. Déposez le fichier ci-dessous (ou ouvrez le lien de partage) et le projet se chargera dans votre propre navigateur.',
        )}
      </p>

      {busy && (
        <div className="mt-8 rounded-xl border border-border bg-surface p-6 text-center text-text-muted">
          {t('Importing…', 'Importation…')}
        </div>
      )}

      {result && !busy && (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="text-3xl">✓</div>
          <h2 className="mt-2 text-lg font-semibold text-emerald-800 dark:text-emerald-300">
            {t('Imported!', 'Importé!')}
          </h2>
          <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-200">
            {t(
              `${result.projects} project · ${result.tasks} task${result.tasks === 1 ? '' : 's'} · ${result.sheets} saved sheet${result.sheets === 1 ? '' : 's'}`,
              `${result.projects} projet · ${result.tasks} tâche${result.tasks === 1 ? '' : 's'} · ${result.sheets} feuille${result.sheets === 1 ? '' : 's'} enregistrée${result.sheets === 1 ? '' : 's'}`,
            )}
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              onClick={goToProject}
              className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-on-primary hover:opacity-90"
            >
              {result.firstProjectId
                ? t('Open the project →', 'Ouvrir le projet →')
                : t('Open My Sheets →', 'Ouvrir Mes feuilles →')}
            </button>
          </div>
        </div>
      )}

      {error && !busy && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          <strong>{t('Could not import.', 'Importation impossible.')}</strong>
          <p className="mt-1">{error}</p>
        </div>
      )}

      {!result && (
        <div className="mt-8 rounded-xl border border-border bg-surface p-6">
          <h2 className="text-lg font-semibold text-text">
            {t('Pick a project file', 'Choisissez un fichier de projet')}
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            {t(
              'Select the .json file the sender downloaded from their project board.',
              'Sélectionnez le fichier .json téléchargé par l’expéditeur depuis son tableau de projet.',
            )}
          </p>
          <div className="mt-4">
            <button
              onClick={() => fileRef.current?.click()}
              className="rounded-lg border border-brand bg-brand/5 px-5 py-2.5 text-sm font-semibold text-brand-text hover:bg-brand/10"
            >
              {t('Choose file…', 'Choisir un fichier…')}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={onFile}
              className="hidden"
            />
          </div>
        </div>
      )}

      <div className="mt-10 rounded-xl border border-dashed border-border bg-surface-2 p-5 text-sm text-text-muted">
        <strong className="block text-text">
          {t('How does this work?', 'Comment ça fonctionne?')}
        </strong>
        <p className="mt-1">
          {t(
            'Everything you import is saved in your own browser only — RenoSheets has no accounts, no servers, no tracking. The shared project becomes yours to edit; if the sender updates their copy, they can send a new file or link and you can import it again to refresh.',
            'Tout ce que vous importez est sauvegardé dans votre navigateur seulement — RenoSheets n’a aucun compte, aucun serveur, aucun suivi. Le projet partagé devient le vôtre; si l’expéditeur met son projet à jour, il peut vous envoyer un nouveau fichier ou lien à réimporter.',
          )}
        </p>
      </div>
    </div>
  )
}
